import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import type { Membership } from '../api/types';
import { reportScreen, useEndViewAs } from '../api/admin';
import { useReviewQueue } from '../api/takes';
import { isDirector, setActiveChoir, useLogout, useMe, useMembership } from '../auth/session';
import { applyLanguage } from '../i18n';
import { shortName } from '../lib/format';
import { PageErrorBoundary } from './Guards';
import { ProfileDialog } from './ProfileDialog';
import styles from './AppLayout.module.css';
import { AudioMenu } from './AudioMenu';
import { FeedbackMenu } from './FeedbackMenu';
import { Brand } from './Logo';

/**
 * The administrator's "view as" session (wireframe 12): whom they see, that nothing can be changed
 * and the session is logged, the minutes left, and "End viewing".
 */
function ViewAsBar({ userName, endsAt, membership }: { userName: string; endsAt: string; membership: Membership | undefined }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const end = useEndViewAs();
  const me = useMe();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, []);
  const minutes = Math.max(0, Math.ceil((new Date(endsAt).getTime() - now) / 60_000));
  // At the time limit the server ends the session: reload who is signed in.
  useEffect(() => {
    if (minutes === 0) void me.refetch();
  }, [minutes]); // eslint-disable-line react-hooks/exhaustive-deps
  const who = membership ? [membership.choirName, roleLabel(membership, t)].join(' · ') : null;
  return (
    <div className={styles.viewAs} role="status" data-testid="view-as-bar">
      <span className={styles.readOnly}>{t('viewAs.readOnly')}</span>
      <span>
        {t('viewAs.viewing')} <strong>{userName}</strong>
        {who && <> · {who}</>}
      </span>
      <span className={styles.viewAsNote}>{t('viewAs.note', { count: minutes })}</span>
      <button
        type="button"
        className={styles.endViewing}
        disabled={end.isPending}
        onClick={() => end.mutate(undefined, { onSuccess: () => navigate('/admin/users', { replace: true }) })}
      >
        {t('viewAs.end')}
      </button>
    </div>
  );
}

function roleLabel(m: Membership | undefined, t: (key: string) => string): string {
  if (!m) return '';
  if (m.roles.includes('DIRECTOR')) return t('roles.DIRECTOR');
  if (m.defaultPart) return t(`parts.${m.defaultPart}`);
  return t(`roles.${m.roles[0] ?? 'CHORIST'}`);
}

export function AppLayout() {
  const { t } = useTranslation();
  const me = useMe().data;
  const membership = useMembership();
  const logout = useLogout();
  const navigate = useNavigate();
  const director = isDirector(membership);
  const location = useLocation();
  const [profileOpen, setProfileOpen] = useState(false);
  const queue = useReviewQueue(membership?.choirId, director);
  const waiting = queue.data?.length ?? 0;

  // The language saved to the account wins over this browser's, on every login and computer.
  // During "view as" the administrator keeps their own language.
  useEffect(() => {
    if (me?.locale && !me.viewAs) applyLanguage(me.locale);
  }, [me?.locale, me?.viewAs]);

  // Each screen shown during "view as" goes into the audit log.
  const viewing = !!me?.viewAs;
  useEffect(() => {
    if (viewing) reportScreen(location.pathname);
  }, [viewing, location.pathname]);

  const navClass = ({ isActive }: { isActive: boolean }) => (isActive ? `${styles.navLink} ${styles.active}` : styles.navLink);

  return (
    <>
      {me?.viewAs && <ViewAsBar userName={me.viewAs.userName} endsAt={me.viewAs.endsAt} membership={membership} />}
      <header className={styles.header}>
        <Brand size={40} className={styles.brand} />
        {/* Someone in several choirs works in one at a time; each keeps its own songs, events and members. */}
        {me && me.memberships.length > 1 && membership && (
          <select
            className={styles.choirSelect}
            aria-label={t('nav.choir')}
            data-testid="choir-switcher"
            value={membership.choirId}
            onChange={(e) => {
              setActiveChoir(e.target.value);
              navigate('/');
            }}
          >
            {me.memberships.map((m) => (
              <option key={m.choirId} value={m.choirId}>
                {m.choirName}
              </option>
            ))}
          </select>
        )}
        <nav className={styles.nav} aria-label={t('nav.main')}>
          {director ? (
            <>
              <NavLink to="/events" className={navClass}>
                {t('nav.events')}
              </NavLink>
              <NavLink to="/review" className={navClass} data-testid="nav-review">
                {t('nav.review')}
                {waiting > 0 && (
                  <span className={styles.badge} aria-label={t('nav.reviewWaiting', { count: waiting })}>
                    {waiting}
                  </span>
                )}
              </NavLink>
              <NavLink to="/library" className={navClass}>
                {t('nav.library')}
              </NavLink>
              <NavLink to="/members" className={navClass}>
                {t('nav.members')}
              </NavLink>
            </>
          ) : (
            <>
              <NavLink to="/" end className={navClass}>
                {t('nav.home')}
              </NavLink>
              <NavLink to="/library" className={navClass}>
                {t('nav.library')}
              </NavLink>
              <NavLink to="/events" className={navClass}>
                {t('nav.events')}
              </NavLink>
              <NavLink to="/members" className={navClass}>
                {t('nav.members')}
              </NavLink>
            </>
          )}
        </nav>
        <div className={styles.user}>
          <FeedbackMenu disabled={!!me?.viewAs} disabledHint={t('viewAs.off')} />
          <AudioMenu />
          {me && (
            <button
              type="button"
              className={styles.userName}
              data-testid="current-user"
              aria-haspopup="dialog"
              title={t('profile.open')}
              onClick={() => setProfileOpen(true)}
            >
              {t('nav.userLabel', { name: shortName(me.displayName), role: roleLabel(membership, t) })}
            </button>
          )}
          <button
            type="button"
            className={styles.logout}
            disabled={logout.isPending}
            onClick={() => logout.mutate(undefined, { onSettled: () => navigate('/login', { replace: true }) })}
          >
            {t('nav.logout')}
          </button>
        </div>
      </header>
      {me && <ProfileDialog me={me} open={profileOpen} onClose={() => setProfileOpen(false)} />}
      <main>
        <PageErrorBoundary resetKey={location.pathname}>
          <Outlet />
        </PageErrorBoundary>
      </main>
    </>
  );
}
